import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor, Smartphone, LogOut, Check, X, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './FollowSettings.css';

function describeDevice(userAgent, t) {
  const ua = userAgent || '';
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  let browser = t('activeSessions.unknownBrowser');
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';

  let os = t('activeSessions.unknownDevice');
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return { label: t('activeSessions.onDevice', { browser, os }), isMobile };
}

function timeAgo(value, t) {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return t('activeSessions.activeJustNow');
  if (minutes < 60) return t('activeSessions.activeMinAgo', { n: minutes });
  if (hours < 24) return t('activeSessions.activeHourAgo', { n: hours });
  if (days < 30) return t('activeSessions.activeDayAgo', { n: days });
  return new Date(value).toLocaleDateString();
}

function ActiveSessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) { setError(t('activeSessions.pleaseLogin')); return; }
      const response = await fetch(`${API_BASE_URL}/auth/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) setSessions(data.sessions || []);
      else setError(data.error || t('activeSessions.failedLoad'));
    } catch {
      setError(t('activeSessions.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (sessionId) => {
    try {
      setRevokingId(sessionId);
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        setSuccessMessage(t('activeSessions.deviceLoggedOut'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || t('activeSessions.failedLogout'));
      }
    } catch {
      setError(t('activeSessions.failedLogout'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm(t('activeSessions.confirmRevokeAll'))) return;
    try {
      setRevokingAll(true);
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/sessions`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSessions((prev) => prev.filter((s) => s.isCurrent));
        setSuccessMessage(t('activeSessions.loggedOutAll'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || t('activeSessions.failedLogoutAll'));
      }
    } catch {
      setError(t('activeSessions.failedLogoutAll'));
    } finally {
      setRevokingAll(false);
    }
  };

  const otherSessions = sessions.filter((s) => !s.isCurrent);

  return (
    <div className="follow-settings-container">
      <button onClick={() => navigate('/settings')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#32614d', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ArrowLeft size={16} /> {t('activeSessions.backToSettings')}
      </button>

      <div className="settings-header">
        <h2>{t('activeSessions.title')}</h2>
        <p className="settings-subtitle">
          {t('activeSessions.subtitle')}
        </p>
      </div>

      {error && (
        <div className="error-message"><X size={20} /><span>{localizeMessage(error)}</span></div>
      )}
      {successMessage && (
        <div className="success-message"><Check size={20} /><span>{successMessage}</span></div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('activeSessions.loading')}</p>
        </div>
      ) : (
        <>
          <div className="settings-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            {sessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 24px', color: '#aaa', fontSize: 14 }}>
                {t('activeSessions.noSessions')}
              </div>
            ) : (
              sessions.map((s) => {
                const device = describeDevice(s.userAgent, t);
                const Icon = device.isMobile ? Smartphone : Monitor;
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f0f9f4', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {device.label}
                        {s.isCurrent && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#e8f5e9', color: '#32614d', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                            <ShieldCheck size={11} /> {t('activeSessions.thisDevice')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{timeAgo(s.lastActiveAt, t)}{s.ip ? ` · ${s.ip}` : ''}</div>
                    </div>
                    {!s.isCurrent && (
                      <button onClick={() => handleRevoke(s.id)} disabled={revokingId === s.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'white', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: revokingId === s.id ? 0.6 : 1, flexShrink: 0 }}>
                        <LogOut size={14} /> {revokingId === s.id ? t('activeSessions.loggingOut') : t('activeSessions.logout')}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {otherSessions.length > 0 && (
            <button onClick={handleRevokeAll} disabled={revokingAll}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: '#fff5f5', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: revokingAll ? 0.6 : 1 }}>
              <LogOut size={15} /> {revokingAll ? t('activeSessions.loggingOut') : t('activeSessions.logoutOtherDevices', { count: otherSessions.length })}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default ActiveSessions;
